/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable max-len */
import React from 'react';
import cx from 'classnames';
import Link from 'next/link';

import { Button } from '@motorway/mw-highway-code';
import { Hyperlink } from '@motorway/mw-highway-code/alpha';

import Routes from 'Utilities/routes';

import styles from '../../StaticContents.module.scss';

const Terms: React.FC = () => (
	<>
		<div className={cx(styles.center, styles.bold, styles['mb-64'])}>
			<h1>MOTORWAY PAY TERMS AND CONDITIONS</h1>
			<p>effective from 29 April 2024</p>
		</div>
		<h3>This Agreement applies to your Motorway Pay Account and you must read it carefully.</h3>
		<p>
			By using your Payment Account you have accepted the terms and conditions of this Agreement, along with the Modulr
			Account Terms and Conditions set out in Schedule 1 and you agree to comply with these. Your rights and obligations
			in relation to your Payment Account are as set out in this Agreement. You can view a copy of this Agreement at any
			time via Pro.
		</p>
		<br />
		<ol className={cx(styles.termsListRoot, styles['mb-64'])}>
			<li>
				<h3>DEFINITIONS</h3>
				<div className={styles.termsKeys}>
					<div>In these Conditions, the following words have the following meanings:</div>
					<div>
						<strong>"Agreement"</strong> means these terms and conditions, as varied from time to time.
					</div>
					<div>
						<strong>"Available Funds"</strong> means the total value of funds that are loaded onto your Payment Account
						minus any Committed Funds.
					</div>
					<div>
						<strong>"CDD"</strong> means the customer due diligence measures required to be undertaken to identify and
						verify your identity in accordance with applicable law and regulation (including without limitation
						anti-money laundering).
					</div>
					<div>
						<strong>"Committed Funds"</strong> means any funds in your Payment Account that have been ringfenced for a
						Transaction or a series of Transactions.
					</div>
					<div>
						<strong>"Dealer"</strong> means a purchaser of vehicles through Pro.
					</div>
					<div>
						<strong>"Force Majeure Event"</strong> means any cause affecting, preventing or hindering the performance by
						a party of its obligations under this Agreement arising from acts, events, omissions or non-events beyond
						its reasonable control, including, without limitation, acts of God, riots, war, acts of terrorism, fire,
						flood, storm or earthquake and any disaster.
					</div>
					<div>
						<strong>"Modulr"</strong> means Modulr Finance Limited and Modulr FS Limited.
					</div>
					<div>
						<strong>"Modulr Account"</strong> means the electronic account provided to you by Modulr together with
						related services under the Modulr Account Terms and Conditions.
					</div>
					<div>
						<strong>"Modulr Account Terms and Conditions"</strong> means (1) the Introduced Client Application Form, (2)
						the Introduced Client Terms of Business and (3) the Modulr Account Terms and Conditions attached at Schedule
						1 which together form a separate agreement between you and Modulr and which govern the terms on which you
						may use the Modulr Account.
					</div>
					<div>
						<strong>"Modulr Finance Limited"</strong> means Modulr Finance Limited, a company registered in England and
						Wales (company registration number 9897957), with its registered office at Scale Space, 58 Wood Lane, London
						W12 7RZ.
					</div>
					<div>
						<strong>"Modulr FS Limited"</strong> means Modulr FS Limited, a company registered in England and Wales
						(company registration number 9897919), with its registered office at Scale Space, 58 Wood Lane, London W12
						7RZ.
					</div>
					<div>
						<strong>"Motorway"</strong> means Motorway Online Ltd, a company registered in England and Wales with
						company registration number 10285711, with its registered office at International House, 24 Holborn Viaduct,
						London, EC1A 2BN.
					</div>
					<div>
						<strong>"OTP"</strong> means the one-time password sent to your registered device.
					</div>
					<div>
						<strong>"Payment Account"</strong> means the electronic account that is provided by Modulr for your use of
						Motorway Pay platform.
					</div>
					<div>
						<strong>"Personal Data"</strong> means any information relating to an identified or identifiable natural
						person ('data subject'); an identifiable natural person is one who can be identified, directly or
						indirectly, in particular by reference to an identifier such as a name, an identification number, location
						data, an online identifier or to one or more factors specific to the physical, physiological, genetic,
						mental, economic, cultural or social identity of that natural person.
					</div>
					<div>
						<strong>"Privacy Policy"</strong> means the privacy policy available at{' '}
						<Link legacyBehavior passHref as="/privacy" href="/static_page">
							<Hyperlink label="https://pro.motorway.co.uk/privacy" variant="secondary" />
						</Link>
						.
					</div>
					<div>
						<strong>"Pro"</strong> means the web application available at{' '}
						<Link legacyBehavior passHref href="/">
							<Hyperlink label="https://pro.motorway.co.uk" variant="secondary" />
						</Link>
						, from which you confirm all Transactions.
					</div>
					<div>
						<strong>"Pro Terms"</strong> means the terms and conditions for the use of Pro available at{' '}
						<Link legacyBehavior passHref as="/terms" href="/static_page">
							<Hyperlink label="https://pro.motorway.co.uk/terms" variant="secondary" />
						</Link>{' '}
						and as may be amended from time to time.
					</div>
					<div>
						<strong>"Purchase Fee"</strong> means the sum(s) payable by you to Motorway in accordance with the Pro
						Terms.
					</div>
					<div>
						<strong>"Security Details"</strong> means any details or credentials which you use to authorise a
						Transaction, confirm your identity, access a device or service or to authorise other actions related to the
						use of your Payment Account (for example, your Pro login information, password, any one-time password).
					</div>
					<div>
						<strong>"Seller"</strong> means an individual who sells their vehicle through Pro to a Dealer.
					</div>
					<div>
						<strong>"Total Vehicle Price"</strong> means the amount displayed on Pro which is the total of the amount
						going to the seller and the finance to be settled.
					</div>
					<div>
						<strong>"Transaction"</strong> means a transaction for a vehicle offered for sale on Pro where you have
						authorised payment from your Payment Account in accordance with section 3.
					</div>
					<div>
						<strong>"Transaction Statement"</strong> means the record of Transactions provided to you by Motorway.
					</div>
					<div>
						<strong>"Transport Fee"</strong> means the sum(s) payable by you to Motorway in accordance with the
						Transport Terms.
					</div>
					<div>
						<strong>"Transport Services"</strong> means the transport services provided to you in accordance with the
						Transport Terms.
					</div>
					<div>
						<strong>"Transport Terms"</strong> means the terms and conditions for the use of Transport Services,
						available on Pro.
					</div>
					<div>
						<strong>"we", "us" or "our"</strong> means Motorway.
					</div>
					<div>
						<strong>"you" and "your"</strong> means the person named on the Payment Account.
					</div>
				</div>
			</li>
			<li>
				<h3>YOUR PAYMENT ACCOUNT</h3>
				<ol>
					<li>Incoming payments will be credited to your Payment Account and then added to your Available Funds.</li>
					<li>Your Payment Account is not transferable, it may only be used by you.</li>
					<li>
						You may only apply for a Payment Account if you are at least 18 years old and acting on behalf of a Dealer
						that is registered on Pro and has been verified by us.
					</li>
					<li>
						Modulr will conduct applicable CDD to identify you. We will keep records of any CDD information and
						documents you provide as part of this process in accordance with our Privacy Policy.
					</li>
					<li>
						Your Payment Account will only become active and funds will only be transferable to your Payment Account
						following completion of the CDD.
					</li>
					<li>
						You undertake to inform us immediately if there are any changes to the information you provided as part of
						the CDD process.
					</li>
					<li>
						Funds will only be transferred into a Payment Account by electronic transfer from a bank account held in the
						United Kingdom.
					</li>
				</ol>
			</li>
			<li>
				<h3>MAKING PAYMENT AND AUTHORISING TRANSACTIONS</h3>
				<ol>
					<li>
						Where you have submitted a payment request by clicking "Proceed with Payment" or "Submit Revised Offer", you
						are authorising Motorway to withdraw from the Available Funds in your Payment Account the Total Vehicle
						Price plus the Purchase Fee and, where applicable, the Transport Fee, as well as any additional fees as
						notified to you by Motorway from time to time.
					</li>
					<li>
						Where you have Available Funds and have submitted a payment request by clicking either "Proceed with
						Payment" or "Submit Revised Offer", Motorway may not make payment if:
						<ol>
							<li>
								your Payment Account has reached any of the Motorway account limits communicated to you from time to
								time;
							</li>
							<li>there is an issue with verifying the Seller’s account details;</li>
							<li>your Payment Account is inactive or suspended or terminated for any reason;</li>
							<li>your Pro account has been frozen or suspended; or</li>
							<li>we are not legally allowed to do so.</li>
						</ol>
					</li>
					<li>
						If you have submitted a payment request by clicking "Proceed with Payment" or "Submit Revised Offer" and
						more than the Total Vehicle Price plus the Purchase Fee and, where applicable, the Transport Fee, was
						withdrawn from your Payment Account, you should contact your Account Manager who will arrange a refund of
						the additional amount.
					</li>
					<li>
						Full or partial refunds from Motorway will appear in your Payment Account within 14 days of confirmation.
					</li>
					<li>
						If you wish to cancel a Transaction or have any concerns about the condition of the vehicle, you should
						reach out to your Motorway Account Manager in the normal way.{' '}
					</li>
				</ol>
			</li>
			<li>
				<h3>TERMINATION AND EXPIRY</h3>
				<ol>
					<li>
						You have the right to close your Payment Account and terminate this Agreement on two months’ notice. You can
						do so by contacting your Account Manager in the first instance. On receipt of your instruction, the Payment
						Operations team will debit the Total Vehicle Price plus any applicable fees in relation to any outstanding
						Transactions. You can then withdraw your unused Available Funds as specified in section 8 of this Agreement.{' '}
					</li>
					<li>
						In our sole discretion, we may close your Payment Account and terminate this Agreement at any point on two
						months’ notice.
					</li>
					<li>
						We may terminate this Agreement immediately and without prior notice in the following circumstances:
						<ol>
							<li>if you have committed a material breach of this Agreement or the Pro Terms;</li>
							<li>
								you use (or allow someone else to use) the Payment Account improperly, illegally or for criminal
								activity;
							</li>
							<li>
								we must do so to comply with any law, regulation or by order of the police or any relevant governmental
								or regulatory authority or court;{' '}
							</li>
							<li>we establish any information we relied upon in allowing you your Payment Account was untrue;</li>
							<li>
								we reasonably consider your use of the Payment Account may damage our systems, hardware or software; or
							</li>
							<li>
								we reasonably believe that you have given access to the Payment Account to someone other than the
								individual authorised to access the Payment Account;
							</li>
						</ol>
					</li>
					<li>
						If we terminate this Agreement for any of the reasons set out in section 4.3 above, we will be entitled but
						not required to immediately debit all Committed Funds plus any applicable fees in relation to any
						outstanding Transactions. You will then be able to withdraw your unused Available Funds in accordance with
						section 8 of this Agreement.
					</li>
				</ol>
			</li>
			<li>
				<h3>KEEPING YOUR PAYMENT ACCOUNT SECURE</h3>
				<ol>
					<li>
						You must not give your Security Details to other people or allow others to access your Payment Account.
					</li>
					<li>
						You should regularly check the Transactions recorded on Pro as well as your Transaction Statements for any
						Transactions or purchases that you have not authorised, and notify your Account Manager as soon as possible.
					</li>
					<li>
						You undertake to let us know without undue delay if you think that your Payment Account has been compromised
						in any way.
					</li>
					<li>
						You undertake to:
						<ol>
							<li>
								notify us immediately if access to the device registered for one time password (OTP) has been
								compromised or the device is no longer in your possession;
							</li>
							<li>close down Pro when you are not using it;</li>
							<li>
								ensure that you are using the latest version of the software for the device you use to access your
								Payment Account and Pro and keep the device itself secure; and
							</li>
							<li>ensure that you are always using the latest version of Pro.</li>
						</ol>
					</li>
					<li>
						We will never ask you to make payments from your Payment Account to any other bank account. During the
						onboarding process, a member of the Payment Operations Team will ask you to share your OTP with them once in
						order to verify your identity. Following completion of onboarding, we will not ask you to share your OTP
						with us again. If anyone, including anyone purporting to be a Motorway representative, asks you to make a
						payment from your Payment account or share your OTP, you should contact your Account Manager immediately.
					</li>
					<li>
						To meet our legal and regulatory obligations, we might sometimes need to ask for more information about you
						and / or your use of your Payment Account. For example, because your normal spending pattern has changed.
						You must provide this information promptly to avoid any issues or delays with our service.
					</li>
					<li>
						We take instances of fraud and criminality on our platform seriously. Your Payment Account may be suspended
						or terminated should we suspect any instance of fraud (including but not limited to allowing others to use
						your Payment Account), money laundering, or concealing proceeds of crime.
					</li>
				</ol>
			</li>
			<li>
				<h3>OUR LIABILITY</h3>
				<ol>
					<li>
						Except as provided in section 6.4 (below), Motorway shall not be liable to you for any indirect or
						consequential loss or damage (including without limitation loss of business, profits or revenues), incurred
						in connection with this Agreement, whether arising in contract, tort (including negligence), breach of
						statutory duty or otherwise.
					</li>
					<li>
						We will not be liable for any loss arising from:
						<ol>
							<li>
								inability to access your Payment Account on your device or by other means. Access to your Payment
								Account will be on an "as available" and "as is" basis;
							</li>
							<li>
								where you acted:
								<ol>
									<li>in an unauthorised or fraudulent manner;</li>
									<li>in such a way as to materially breach this Agreement; or</li>
									<li>negligently, </li>
								</ol>
							</li>
							<li>or on the occurrence of a Force Majeure Event.</li>
						</ol>
					</li>
					<li>
						Motorway’s liability in relation to the Transaction and purchase of the vehicle and use of Pro are as
						provided in the Pro Terms, as may be amended from time to time.
					</li>
					<li>
						Where sums are incorrectly deducted from your Available Funds and in all other circumstances, involving
						non-execution, defective or late execution of a Transaction, Motorway’s liability shall be limited to
						payment to you of misdirected funds together with any charges incurred.
					</li>
					<li>
						In relation to unauthorised Transactions that were caused by an act or omission on the part of Motorway,
						Motorway’s liability shall not exceed to payment to you of the total amounts that have been lost, stolen or
						misused, together with any charges incurred.
					</li>
				</ol>
			</li>
			<li>
				<h3>YOUR LIABILITY FOR TRANSACTIONS</h3>
				<ol>
					<li>
						You will compensate Motorway in full (and our officers, directors, agents, subsidiaries and employees) for
						any losses or costs, including reasonable legal fees, we incur arising out of any breach by you of this
						Agreement, your improper use of our service or your breach of any law or the rights of a third party.
					</li>
					<li>
						If you believe that a Transaction has been incorrectly executed or was not authorised by you, you must
						inform us immediately via your Account Manager. Failure to notify us immediately on becoming aware of an
						unauthorised or incorrectly executed Transaction could result in you losing your entitlement to have the
						matter corrected.
					</li>
					<li>
						You will be liable for all unauthorised Transactions that arise from the use of Security Details by a third
						party and if you fail to keep the Security Details of the Payment Account safe.
					</li>
					<li>
						Where we establish that a Transaction was not authorised by you and you have otherwise taken all reasonable
						steps to maintain the security of the Account and integrity of the Security Details and you have notified us
						in a timely manner, we shall refund to you the full amount debited without authorisation.
					</li>
					<li>
						You will be liable for all losses incurred in respect of a Transaction made by us which was not authorised
						by you, if you have acted fraudulently, or have intentionally or negligently failed to comply with the
						obligations set out under section 5 (Keeping Your Payment Account Secure).
					</li>
					<li>
						You agree to comply with, and not cause a third party to violate, all applicable laws, regulations, rules
						and terms and conditions in connection with the use of your Payment Account.{' '}
					</li>
					<li>
						You understand that Modulr has its own terms and conditions relating to the use of your Payment Account.
						Failure to abide by Modulr’s terms and conditions may result in termination of your Payment Account or other
						actions taken by Modulr, and you agree that Motorway has no control over, or responsibility or liability
						for, such actions.
					</li>
				</ol>
			</li>
			<li>
				<h3>YOUR WITHDRAWAL OF FUNDS</h3>
				<ol>
					<li>
						You may request a withdrawal of your Available Funds, whether in full or in part, by either:
						<ol>
							<li>
								following the withdrawal procedure within your Motorway Pay wallet by selecting “Withdraw funds”; or
							</li>
							<li> contacting your Account Manager, indicating the amount to be withdrawn.</li>
						</ol>
					</li>
					<li>
						Within 2 hours from receipt of a request made directly via your wallet in accordance with Clause 8.1.1, or
						within 1-2 working days of receipt of a request in accordance with Clause 8.1.2., we will aim to return your
						Available Funds (in full or in part, as appropriate) to the bank account designated during onboarding
						(subject to Clause 8.3), to the extent that we are legally or practically able to do so. In the case of
						withdrawals made in accordance with Clause 8.1.1., we are reliant on the faster payments network and
						therefore whilst withdrawals are likely to be instantaneous, we cannot guarantee that withdrawals will be
						completed within 2 hours. Before we do so, we may need to verify your identity (for example, by OTP where a
						withdrawal is requested in accordance with Clause 8.1.1.) in order to satisfy applicable law and we reserve
						the right to withhold funds until we have been able to do so and/or if we have a reasonable concern
						regarding fraud or other security issues.
					</li>
					<li>
						If the bank account designated by you during onboarding is no longer correct, you can update your details by
						contacting your Account Manager, who may ask you to provide further information to verify this request.
					</li>
				</ol>
			</li>
			<li>
				<h3>CHANGES TO THIS AGREEMENT</h3>
				<ol>
					<li>
						We may update or amend this Agreement from time to time. We will give you reasonable prior written notice of
						any material change unless the change is required due to a change in relevant legislation or regulation and
						we are prevented from doing so. You will be taken to have accepted any notified change to this Agreement
						unless you notify us that you do not agree prior to the change(s) taking effect. We’d be sad to see you go,
						but if you do not wish to agree to such changes to the Agreement, you can stop using Motorway Pay and close
						your Motorway Pay Account.
					</li>
					<li>The most up-to-date version of this Agreement will always be available on Pro.</li>
					<li>
						If any provision of this Agreement is or becomes invalid, illegal or unenforceable, it shall be deemed
						deleted, but that shall not affect the validity and enforceability of the rest of this Agreement.{' '}
					</li>
				</ol>
			</li>
			<li>
				<h3>YOUR DETAILS</h3>
				<ol>
					<li>
						You agree that we may contact you by telephone, mobile, SMS or email at the contact details you provide us.
					</li>
					<li>
						You undertake to let us know as soon as possible of any changes to your name, address, telephone number,
						mobile number or e-mail address. You can do this by contacting your Account Manager in the first instance.
						We may request proof of any changes to your details. We will not be liable to you if you have changed your
						contact details and you have not told us.
					</li>
					<li>
						If we contact you in relation to your Payment Account, we will use the most recent contact details you have
						provided to us. Any email or SMS to you will be treated as being received as soon as it is sent by us using
						the details you have most recently provided.
					</li>
					<li>
						If you do not tell us promptly about any change to your contact details, the security of your information
						may be at risk and you may not receive important information from us, including notices about changes to
						this Agreement.
					</li>
					<li>
						If you do not provide us with a valid mobile number, this may restrict your ability to use our payment
						system. This is because we require you to take additional security steps to confirm your identity before
						your payment is accepted. For example, you may be asked to enter a one-time passcode which may be sent to
						your mobile phone.{' '}
					</li>
				</ol>
			</li>
			<li>
				<h3>DATA PROTECTION</h3>
				<p>
					All Personal Data collected by us or our service providers and passed to us in relation to this Agreement will
					be handled in accordance with the Privacy Policy and in accordance with all applicable laws and regulations.
				</p>
			</li>
			<li>
				<h3>GENERAL</h3>
				<ol>
					<li>
						This Agreement and any obligations arising out or in connection with it are governed and construed by the
						laws of England and Wales. Parties agree to submit to the exclusive jurisdiction of the English courts in
						relation to any dispute or claim arising out of or in connection with this Agreement. This condition still
						allows us to commence proceedings before any other courts in a different jurisdiction where we are permitted
						to do so under applicable law.
					</li>
					<li>
						If any provision of this Agreement is deemed illegal or unenforceable, the remaining provisions will
						continue in full force and effect.{' '}
					</li>
					<li>
						Any delay or failure by us to exercise any right or remedy under this Agreement shall not be construed as a
						waiver of that right or remedy nor prevent us from enforcing such right or remedy at a later date.{' '}
					</li>
				</ol>
			</li>
		</ol>
		<div className={styles.center}>
			<Link legacyBehavior passHref href={Routes.MODULR_PAYMENT_TERMS.href}>
				<Button
					as="a"
					icon="chevron"
					label="Modulr Account Terms and Conditions (Schedule 1)"
					target="_blank"
					variant="tertiary"
				/>
			</Link>
		</div>
	</>
);

export default Terms;
